/**
 * How a program runs: the order the sessions come in, and how many a day.
 *
 * TWO NUMBERS, NOT ONE, and the distinction is the whole reason this panel
 * exists in the shape it does (DECISIONS.md 2026-08-22).
 *
 *   Aim for          What is being prescribed. The patient IS told this — it is
 *                    their treatment plan, and adherence is measured against
 *                    it. A patient who does one of an aim of one has done their
 *                    therapy completely, not a fraction of anything.
 *
 *   Never more than  The ceiling. The patient is NEVER told this. Anything
 *                    between the aim and the ceiling is optional extra work,
 *                    and the moment a patient screen can say "2 of 5" it has
 *                    become a progress bar — which in this population is an
 *                    instruction to reach 5 (constraint 3).
 *
 * Both default to 1, so a new program behaves exactly as programs did before
 * this panel existed, and raising the ceiling is always a deliberate act
 * (constraint 5: under-dosing is the default posture).
 *
 * NO ADVISORIES HERE. Phase 3 removed every one of them because they
 * second-guessed a clinical decision with none of the context behind it. The
 * only rule enforced is that the ceiling cannot sit below the aim, which is not
 * a judgment about dosing — it is a prescription the software would then refuse
 * to deliver. The gap in particular has no floor: several short sessions spread
 * across a day is distributed practice, and how far apart they need to be is
 * the doctor's call, not this form's.
 */

import type { ScheduleDraft } from '../../api.js';
import { Panel } from '../../components/Panel.js';
import styles from './SchedulePanel.module.css';

const PER_DAY_CHOICES = [1, 2, 3, 4, 5, 6] as const;

export interface SchedulePanelProps {
  schedule: ScheduleDraft;
  /** Labels of the sessions that currently exist, in order. */
  labels: readonly string[];
  onChange: (next: ScheduleDraft) => void;
}

export function SchedulePanel({ schedule, labels, onChange }: SchedulePanelProps) {
  const rotation = schedule.rotation.length > 0 ? schedule.rotation : [...labels];
  const ceilingBelowAim = schedule.maxPerDay < schedule.aimPerDay;

  const set = (patch: Partial<ScheduleDraft>) => onChange({ ...schedule, ...patch });

  return (
    <Panel
      title="How this program runs"
      summary={shutSummary(rotation, schedule)}
    >
      <div className={styles.row}>
        <label className={styles.field}>
          <span className={styles.label}>Aim for</span>
          <select
            className={styles.select}
            value={schedule.aimPerDay}
            onChange={(e) => {
              const aim = Number(e.target.value);
              // Raising the aim past the ceiling would prescribe a dose the app
              // refuses to deliver, so the ceiling comes with it.
              set({ aimPerDay: aim, maxPerDay: Math.max(aim, schedule.maxPerDay) });
            }}
          >
            {PER_DAY_CHOICES.map((n) => (
              <option key={n} value={n}>
                {n} a day
              </option>
            ))}
          </select>
          <span className={styles.help}>
            What you are asking for. The patient is told this, and it is what
            adherence is measured against.
          </span>
        </label>

        <label className={styles.field}>
          <span className={styles.label}>Never more than</span>
          <select
            className={styles.select}
            value={schedule.maxPerDay}
            onChange={(e) => set({ maxPerDay: Number(e.target.value) })}
          >
            {PER_DAY_CHOICES.map((n) => (
              <option key={n} value={n} disabled={n < schedule.aimPerDay}>
                {n} a day
              </option>
            ))}
          </select>
          <span className={styles.help}>
            The limit. Never shown to the patient — anything above the aim is
            offered as optional, without a number attached to it.
          </span>
        </label>

        <label className={styles.field}>
          <span className={styles.label}>At least</span>
          <div className={styles.gapRow}>
            <input
              className={styles.number}
              type="number"
              min={0}
              max={24}
              step={1}
              value={schedule.minGapHours}
              onChange={(e) => {
                const raw = Number(e.target.value);
                if (Number.isFinite(raw)) set({ minGapHours: Math.min(24, Math.max(0, raw)) });
              }}
            />
            <span className={styles.unit}>hours between sessions</span>
          </div>
          <span className={styles.help}>
            What keeps several short sessions spread through the day rather than
            run back to back.
          </span>
        </label>
      </div>

      {ceilingBelowAim && (
        <p className={styles.blocking} role="alert">
          The limit is below what you are asking for, so the last session of the
          day could never be done. Raise the limit or lower the aim.
        </p>
      )}

      <div className={styles.rotation}>
        <span className={styles.label}>Order</span>
        <p className={styles.help}>
          Sessions run in this order and start again at the beginning. It
          advances when a session is <em>completed</em>, not by the calendar, so
          a missed day does not skip one. Add a session twice to run it twice as
          often.
        </p>

        <ol className={styles.sequence}>
          {rotation.map((label, index) => (
            <li key={`${label}-${index}`} className={styles.step}>
              {label}
              <button
                type="button"
                className={styles.removeStep}
                aria-label={`Remove session ${label} from position ${index + 1}`}
                onClick={() => {
                  const next = rotation.filter((_, i) => i !== index);
                  set({ rotation: next.length > 0 ? next : [...labels] });
                }}
              >
                ×
              </button>
            </li>
          ))}
        </ol>

        <div className={styles.addRow}>
          <span className={styles.addLabel}>Add to the order:</span>
          {labels.map((label) => (
            <button
              key={label}
              type="button"
              className={styles.addStep}
              onClick={() => set({ rotation: [...rotation, label] })}
            >
              {label}
            </button>
          ))}
          <button
            type="button"
            className={styles.reset}
            onClick={() => set({ rotation: [...labels] })}
          >
            Reset
          </button>
        </div>

        <p className={styles.summary}>{describe(rotation, schedule.aimPerDay)}</p>
      </div>
    </Panel>
  );
}

/**
 * The dose in one line, for when the panel is shut. The ceiling is included
 * because this is the doctor's screen — it is only the PATIENT who is never
 * shown it (constraint 3).
 */
function shutSummary(rotation: readonly string[], schedule: ScheduleDraft): string {
  const aim = schedule.aimPerDay === 1 ? '1 a day' : `${schedule.aimPerDay} a day`;
  const ceiling =
    schedule.maxPerDay > schedule.aimPerDay ? `, up to ${schedule.maxPerDay}` : '';
  const order = rotation.length > 0 ? rotation.join(' → ') : 'no sessions yet';
  return `${aim}${ceiling} · ${order}`;
}

/**
 * The order in a sentence, because a list of letters is easy to misread and the
 * consequence of misreading it is a patient doing the wrong therapy.
 */
function describe(rotation: readonly string[], aimPerDay: number): string {
  if (rotation.length === 0) return 'No sessions yet.';
  const order = rotation.join(' → ');
  const perDay =
    aimPerDay === 1 ? 'one session a day' : `${aimPerDay} sessions a day`;
  if (rotation.length === 1) {
    return `Session ${rotation[0]} every time — ${perDay}.`;
  }
  return `${order}, then back to ${rotation[0]} — ${perDay}.`;
}
